import React from 'react';
import PropTypes from 'prop-types';
import { Field, useFormikContext } from 'formik';
import { Button } from 'primereact/button';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import {
  LegacyInputTextComponent,
  DropdownWithValidation,
  TextAreaWithValidation,
  FormikCheckbox,
} from '../../../components/prime-formik';
import { GENDER_ARRAY } from '../../../utils/Gr8piConfig';
import FormLanguageSelector from '../../../components/formik-components/FormLanguageSelector';
import { renderAsyncSelector } from '../../../components/formik-components/FormAsyncSelector';
import i18n from './i18n';
import NewRespondentAriFields from './NewRespondentAriFields';
import Debug from '../../../components/Debug';
import useSubscriptionExpiredRestrictions from '../../../hooks/useSubscriptionExpiredRestrictions';
import { RespondentType } from '../../../shared/Constants';

const i18nMessages = defineMessages({
  resInternal: { id: 'respondent.internal', defaultMessage: 'Internal respondents' },
  resExternal: { id: 'respondent.external', defaultMessage: 'External respondents' },
});

const NewRespondentForm = ({ preferedLocales, jobSearching, jobs, searchJob, onCancel }) => {
  const { formatMessage } = useIntl();
  const { handleSubmit, setFieldValue, resetForm, isSubmitting } = useFormikContext();
  const { subscriptionIsExpired } = useSubscriptionExpiredRestrictions();

  const genderOptions = GENDER_ARRAY.map((key) => ({
    value: key,
    label: formatMessage(i18n[`gender_${key}`]),
  }));
  const respondentTypeList = [
    {
      value: RespondentType.INTERNAL,
      label: formatMessage(i18nMessages.resInternal),
    },
    {
      value: RespondentType.EXTERNAL,
      label: formatMessage(i18nMessages.resExternal),
    },
  ];
  const saveAndAddText = formatMessage(i18n.saveAndAdd);

  return (
    <>
      <form className="form" onSubmit={handleSubmit}>
        <div className="p-grid">
          <div className="p-sm-12 p-md-6">
            <div className="p-grid p-fluid">
              <div className="p-col-12">
                <label className="field-label" htmlFor="firstName">
                  <FormattedMessage id="lbl.first_name" defaultMessage="First Name" />
                </label>
              </div>
              <div className="p-col-12">
                <Field
                  id="firstName"
                  name="firstName"
                  component={LegacyInputTextComponent}
                  placeholder={formatMessage(i18n.fNamePlaceholder)}
                />
              </div>
            </div>
            <div className="p-grid p-fluid">
              <div className="p-col-12">
                <label className="field-label" htmlFor="lastName">
                  <FormattedMessage id="lbl.last_name" defaultMessage="Last Name" />
                </label>
              </div>
              <div className="p-col-12">
                <Field
                  type="text"
                  name="lastName"
                  id="lastName"
                  placeholder={formatMessage(i18n.lNamePlaceholder)}
                  component={LegacyInputTextComponent}
                />
              </div>
            </div>
            <div className="p-grid p-fluid">
              <div className="p-col-12">
                <label className="field-label" htmlFor="email">
                  <FormattedMessage id="attrs.email" defaultMessage="Email" />
                </label>
                <Field
                  id="email"
                  name="email"
                  component={LegacyInputTextComponent}
                  placeholder={formatMessage(i18n.emailPlaceholder)}
                />
              </div>
            </div>
            <div className="p-grid p-fluid">
              <div className="p-col-12">
                <div className="form-group">
                  <label className="field-label" htmlFor="respondent-type">
                    <FormattedMessage id="attrs.gender" defaultMessage="Gender" />
                  </label>
                  <Field
                    id="gender"
                    name="gender"
                    component={DropdownWithValidation}
                    options={genderOptions}
                    placeholder={`- ${formatMessage(i18n.choose)} -`}
                  />
                </div>
              </div>
            </div>
            <div className="p-grid p-fluid">
              <div className="p-col-12">
                <div className="form-group">
                  <label className="field-label" htmlFor="respondent-type">
                    <FormattedMessage id="attrs.candidateType" defaultMessage="Respondent type" />
                  </label>
                  <Field
                    id="type"
                    name="type"
                    component={DropdownWithValidation}
                    options={respondentTypeList}
                    placeholder={`- ${formatMessage(i18n.choose)} -`}
                  />
                </div>
              </div>
            </div>
            <div className="p-grid p-fluid">
              <div className="p-col-12">
                <label className="field-label" htmlFor="locale">
                  <FormattedMessage id="attrs.preferredLanguage" defaultMessage="Preferred language" />
                </label>
                <Field id="locale" name="locale" component={FormLanguageSelector} options={preferedLocales} />
              </div>
            </div>
          </div>
          <div className="p-sm-12 p-md-6">
            <div className="p-grid p-fluid">
              <div className="p-col-12">
                <label className="field-label" htmlFor="notes">
                  <FormattedMessage id="attrs.notes" defaultMessage="Notes" />
                </label>
                <Field className="form-control" id="notes" name="notes" component={TextAreaWithValidation} rows={5} />
              </div>
            </div>
            <div className="p-grid">
              <div className="p-col-12">
                <div className="form-group">
                  <label className="field-label" htmlFor="superlink-jobProfileId">
                    <FormattedMessage id="attrs.targetJob" defaultMessage="Target Job Profile" />
                  </label>
                  <Field
                    id="job-profile-id"
                    name="targetJob"
                    component={renderAsyncSelector}
                    processing={jobSearching}
                    data={jobs}
                    filter
                    valueField="id"
                    textField="name"
                    onSearch={searchJob}
                    setFieldValue={setFieldValue}
                    placeholder={`- ${formatMessage(i18n.choose)} -`}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="p-grid">
          <div className="p-sm-12">
            <NewRespondentAriFields />
          </div>
          <div className="p-sm-12">
            <Field
              id="saveAndAdd"
              name="saveAndAdd"
              component={FormikCheckbox}
              labelText={saveAndAddText}
              setFieldValue={setFieldValue}
            />
          </div>
          <div className="p-sm-12">
            <Button
              type="submit"
              disabled={isSubmitting || subscriptionIsExpired}
              label={formatMessage(i18n.save)}
              icon={isSubmitting ? 'pi pi-spin pi-spinner' : ''}
            />
            <Button
              type="button"
              className="p-button-secondary"
              disabled={isSubmitting}
              label={formatMessage(i18n.cancel)}
              onClick={() => {
                resetForm();
                onCancel();
              }}
            />
          </div>
        </div>
        <Debug />
      </form>
    </>
  );
};

NewRespondentForm.propTypes = {
  preferedLocales: PropTypes.arrayOf(PropTypes.any).isRequired,
  jobs: PropTypes.arrayOf(PropTypes.any).isRequired,
  jobSearching: PropTypes.bool.isRequired,
  searchJob: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default NewRespondentForm;
